import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { NewsletterCategoryClient, NewsletterCategoryDTO } from '../services/apiClient';
import { API_BASE_URL, MAX_DROPDOWN_PAGE_SIZE } from '../config/config';
import axiosInstance from '../services/axiosInstance';

// Cached query for dropdowns (mirrors useMemberCategories).
export const useNewsletterCategories = () => {
    const apiClient = new NewsletterCategoryClient(API_BASE_URL, axiosInstance);
    return useQuery({
        queryKey: ['newsletterCategories'],
        queryFn: async () => {
            const response = await apiClient.searchNewsletterCategories({
                page: 1,
                pageSize: MAX_DROPDOWN_PAGE_SIZE,
                sortBy: 'DisplayPosition',
            });
            return response?.data?.results || [];
        },
        staleTime: 4 * 60 * 60 * 1000, // 4 hours
        refetchOnReconnect: true,
    });
};

export const useNewsletterCategoryApi = () => {
    const apiClient = new NewsletterCategoryClient(API_BASE_URL, axiosInstance);

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const create = async (data: NewsletterCategoryDTO) => {
        setIsLoading(true);
        try {
            await apiClient.createNewsletterCategory(data);
        } catch (err: any) {
            setError(err.message || 'Error creating NewsletterCategory');
        } finally {
            setIsLoading(false);
        }
    };

    const edit = async (id: string, data: NewsletterCategoryDTO) => {
        setIsLoading(true);
        try {
            await apiClient.updateNewsletterCategory(id, data);
        } catch (err: any) {
            setError(err.message || 'Error updating NewsletterCategory');
        } finally {
            setIsLoading(false);
        }
    };

    const getById = async (id: string) => {
        setIsLoading(true);
        try {
            return await apiClient.getNewsletterCategoryById(id);
        } catch (err: any) {
            setError(err.message || 'Error fetching NewsletterCategory');
        } finally {
            setIsLoading(false);
        }
    };

    const search = async (filters: any) => {
        setIsLoading(true);
        try {
            return await apiClient.searchNewsletterCategories(filters);
        } catch (err: any) {
            setError(err.message || 'Error searching NewsletterCategories');
        } finally {
            setIsLoading(false);
        }
    };

    const remove = async (id: string) => {
        setIsLoading(true);
        try {
            await apiClient.deleteNewsletterCategory(id);
        } catch (err: any) {
            setError(err.message || 'Error deleting NewsletterCategory');
        } finally {
            setIsLoading(false);
        }
    };

    return { create, edit, getById, search, remove, isLoading, error };
};